const STORAGE_KEY = 'rithmo_draft_meetings'

export type DraftParticipant = {
  id?: string
  name?: string
  email: string
  role?: string
}

export type DraftAgendaItem = {
  id: string
  title: string
  duration: number
  order: number
}

export type RecurrenceRuleDraft = {
  type: number
  repeat_interval?: number
  weekly_days?: string
  end_times?: number
  end_date_time?: string
  never_ends?: boolean
}

export type DraftMeeting = {
  id: string
  title: string
  description?: string
  agenda?: DraftAgendaItem[]
  participants?: DraftParticipant[]
  startTime?: string
  endTime?: string
  duration?: number
  updatedAt: string
  meetingType?: string
  isRecurring?: boolean
  recurringSeriesId?: string | null
  recurrenceRule?: RecurrenceRuleDraft | null
}

const canUseStorage = () => typeof localStorage !== 'undefined'

const readDrafts = (): DraftMeeting[] => {
  if (!canUseStorage()) return []
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

const writeDrafts = (drafts: DraftMeeting[]) => {
  if (!canUseStorage()) return
  localStorage.setItem(STORAGE_KEY, JSON.stringify(drafts))
}

export const getDraftMeetings = (): DraftMeeting[] => readDrafts()

export const getDraftMeeting = (id: string): DraftMeeting | null =>
  readDrafts().find((draft) => draft.id === id) ?? null

export const upsertDraftMeeting = (draft: DraftMeeting): DraftMeeting[] => {
  const drafts = readDrafts()
  const index = drafts.findIndex((item) => item.id === draft.id)
  const updatedDraft = {
    ...drafts[index],
    ...draft,
    updatedAt: draft.updatedAt || new Date().toISOString(),
  }
  if (index >= 0) {
    drafts[index] = updatedDraft
  } else {
    drafts.unshift(updatedDraft)
  }
  writeDrafts(drafts)
  return drafts
}

export const removeDraftMeeting = (id: string): DraftMeeting[] => {
  const drafts = readDrafts().filter((draft) => draft.id !== id)
  writeDrafts(drafts)
  return drafts
}
